import { Platform } from '@angular/cdk/platform';
import {
    AfterViewInit,
    Directive,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    Renderer2,
    SimpleChanges,
    ViewContainerRef,
} from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';

import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { EnvironmentService } from '@core/services/environment.service';
import { PlatformService } from '@core/services/platform/platform.service';
import { UiService } from '@core/services/ui.service';
import { HlsService } from '@features/cms-render/ui/services/hls/hls.service';

/**
 * Directive to play a video when it becomes visible in the viewport.
 */
@Directive({
    selector: '[cmsPlayOnView]',
})
export class CmsPlayOnViewDirective implements AfterViewInit, OnChanges, OnDestroy {
    @Input() public cmsVideoUrl: string;

    @Input() public videoElement: HTMLVideoElement;

    private observer: IntersectionObserver;

    private isMobile: boolean = false;

    private videoHasAudio = false;

    private audioSource: MediaElementAudioSourceNode | any;

    private onloadeddata: () => Promise<boolean>;

    private controlsButtonAdded = false;

    private Hls;

    constructor(
        private readonly vcRef: ViewContainerRef,
        private readonly environmentService: EnvironmentService,
        private readonly uiService: UiService,
        private readonly renderer: Renderer2,
        private readonly platform: Platform,
        private readonly hlsService: HlsService,
        @Inject(WINDOW) private readonly window: CommonWindow,
        private readonly platformService: PlatformService,
    ) { }

    /**
     * Lifecycle hook that is called when the input properties of the directive change.
     * @param changes - The changed input properties.
     */
    public ngOnChanges(changes: SimpleChanges) {
        if (changes.cmsVideoUrl?.currentValue !== changes.cmsVideoUrl?.previousValue) {
            this.initVideo();
        }
    }

    /**
     * Lifecycle hook that is called after the component's view has been initialized.
     */
    public ngAfterViewInit(): void {
        this.isMobile = this.uiService.ui.device.mobile;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        this.cmsVideoUrl && this.initVideo();
    }

    /**
     * Initializes the video playback logic.
     */
    @BrowserOnly<CmsPlayOnViewDirective, undefined>(undefined)
    public initVideo() {
        this.setAttributesToVideo();

        const elToObserve = this.videoElement.parentElement;
        if (this.platformService.isBrowser()) {
            this.observer = new IntersectionObserver(() => { }, { threshold: [0, 0.1, 0.9, 1] });
            this.observer.observe(elToObserve);
        }

        if (this.isHLSVideo(this.cmsVideoUrl)) {
            this.loadHls();
        } else {
            this.setVideoMp4();
        }
    }

    /**
     * Checks if the video URL is an HLS video.
     * @param path - The video URL.
     * @returns True if the video URL is an HLS video, false otherwise.
     */
    private isHLSVideo(path: string): boolean {
        return path.endsWith('.m3u8') || path.includes('.m3u8?');
    }

    /**
     * Sets the video source to an MP4 format.
     */
    private setVideoMp4(): void {
        this.videoElement.src = this.cmsVideoUrl;
    }

    /**
     * Gets the HLS video URL with optional replacement of file extensions.
     * @param replace - Flag to indicate whether to replace the file extension or not.
     * @returns The HLS video URL.
     */
    private getHlsVideoPath(replace = true): string {
        const regexHls = /mp4/i;
        const domain = new URL(this.cmsVideoUrl).origin;
        const url = this.cmsVideoUrl.replace(domain, this.environmentService.getDomainBasedOnEnvironment(domain));
        if (!replace) {
            return url;
        }
        return this.cmsVideoUrl.replace(regexHls, 'm3u8');
    }

    /**
     * Loads the HLS video and initializes the HLS playback logic.
     */
    @BrowserOnly<CmsPlayOnViewDirective, undefined>(undefined)
    private loadHls(): void {
        this.hlsService.hlsLibrary$.subscribe((hlsLibrary) => {
            this.Hls = hlsLibrary;
            if (this.videoElement.canPlayType('application/vnd.apple.mpegurl') && !this.Hls.isSupported()) {
                this.videoElement.src = this.getHlsVideoPath();
                this.videoElement.onerror = () => {
                    this.setVideoMp4();
                };
            } else if (this.Hls.isSupported()) {
                // funcionalidad Duplicada video-play-on-view

                const hls = new this.Hls();

                hls.config.maxBufferLength = 5;
                hls.config.maxBufferSize = 5;

                hls.attachMedia(this.videoElement);
                hls.loadSource(this.getHlsVideoPath());

                hls.on(this.Hls.Events.ERROR, (event, data) => {
                    const errorType = data.type;
                    console.error('Error HLS', errorType);
                });
            } else {
                this.setVideoMp4();
            }
        });
    }

    /**
     * Checks if the video has audio tracks.
     * @returns A promise that resolves to void when the check is complete.
     */
    private async checkIsHasAudio(): Promise<void> {
        const video = this.videoElement as any;
        if (this.platform.FIREFOX) {
            this.videoHasAudio = video.mozHasAudio;
        } else if (this.platform.BLINK) {
            this.videoHasAudio = Boolean(video.webkitAudioDecodedByteCount && video.webkitAudioDecodedByteCount !== 0);
        } else if (this.platform.SAFARI) {
            this.videoHasAudio = await this.safariCheckIsVideoHasAudio();
        } else {
            this.videoHasAudio = video.mozHasAudio
                || Boolean(video.webkitAudioDecodedByteCount && video.webkitAudioDecodedByteCount !== 0)
                || Boolean(video?.audioTracks?.length);
        }
    }

    /**
     * Sets the default attributes for the video element.
     */
    private setAttributesToVideo(): void {
        const attributes = {
            playsinline: '',
            loop: '',
            autoplay: 'this.play()',
            oncanplay: 'this.play()',
            onloadedmetadata: 'this.muted = true',
        };
        Object.keys(attributes).forEach((attr) => {
            this.renderer.setAttribute(this.videoElement, attr, attributes[attr]);
        });

        this.loadVideoData();
    }

    /**
     * Checks if the video has audio tracks using a Safari-specific patch.
     * @returns A promise that resolves to a boolean indicating whether the video has audio tracks or not.
     */
    @BrowserOnly<CmsPlayOnViewDirective, boolean>(false)
    private safariCheckIsVideoHasAudio(): Promise<boolean> {
        return new Promise((resolve) => {
            const audioCtx = new ((this.window as any).AudioContext || (this.window as any).webkitAudioContext)();
            if (!this.audioSource) {
                this.audioSource = true;
                const request = new XMLHttpRequest();
                request.open('GET', this.getHlsVideoPath(false), true);
                request.responseType = 'arraybuffer';

                request.onload = () => {
                    const audioData = request.response;
                    audioCtx
                        .decodeAudioData(audioData)
                        .then(() => {
                            resolve(true);
                        })
                        .catch(() => {
                            resolve(false);
                        });
                };

                request.send();
            }
        });
    }

    /**
     * Loads the video data and performs checks on the video's audio tracks.
     */
    private loadVideoData() {
        this.onloadeddata = async () => {
            await this.checkIsHasAudio();
            if (this.videoHasAudio && !this.controlsButtonAdded) {
                this.controlsButtonAdded = true;
            }
            return true;
        };
        this.videoElement.onloadeddata = this.onloadeddata;
    }

    /**
     * Cleans up resources when the directive is destroyed.
     */
    public ngOnDestroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}
